import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ChevronLeft, User, Mail, Building2, Ticket, AlertTriangle,
  Clock, CheckCircle2, Pause, XCircle, ArrowUpCircle, ArrowRightCircle, ArrowDownCircle,
} from "lucide-react";

const STATUS_MAP = {
  abierto: { label: "Abierto", color: "bg-blue-500 text-white" },
  en_progreso: { label: "En Progreso", color: "bg-amber-500 text-white" },
  en_espera: { label: "En Espera", color: "bg-purple-500 text-white" },
  resuelto: { label: "Resuelto", color: "bg-green-500 text-white" },
  cerrado: { label: "Cerrado", color: "bg-slate-500 text-white" },
};

const PRIORITY_MAP = {
  baja: { label: "Baja", color: "bg-slate-100 text-slate-700", icon: ArrowDownCircle },
  media: { label: "Media", color: "bg-blue-100 text-blue-700", icon: ArrowRightCircle },
  alta: { label: "Alta", color: "bg-orange-100 text-orange-700", icon: ArrowUpCircle },
  urgente: { label: "Urgente", color: "bg-red-100 text-red-700", icon: AlertTriangle },
};

const MONTHS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const HelpdeskContactoDetalle = () => {
  const { contactoId } = useParams();
  const navigate = useNavigate();
  const [contacto, setContacto] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterMonth, setFilterMonth] = useState("all");
  const [filterYear, setFilterYear] = useState("all");

  useEffect(() => {
    fetchData();
  }, [contactoId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [contactosRes, ticketsRes] = await Promise.all([
        axios.get(`${API}/helpdesk/contactos`, { withCredentials: true }),
        axios.get(`${API}/tickets`, { withCredentials: true }),
      ]);

      const decodedId = decodeURIComponent(contactoId);
      const c = contactosRes.data.find(x => x.id === decodedId);
      if (!c) {
        toast.error("Contacto no encontrado");
        navigate("/helpdesk/contactos");
        return;
      }
      setContacto(c);

      const contactTickets = ticketsRes.data.filter(t =>
        t.created_by_name === c.nombre && (t.created_by_email || "") === (c.email || "")
      );
      setTickets(contactTickets);
    } catch (err) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const years = [...new Set(tickets.map(t => new Date(t.created_at).getFullYear()))].sort((a, b) => b - a);

  const filtered = tickets.filter(t => {
    if (filterStatus !== "all" && t.status !== filterStatus) return false;
    if (filterMonth !== "all" || filterYear !== "all") {
      const d = new Date(t.created_at);
      if (filterYear !== "all" && d.getFullYear() !== parseInt(filterYear)) return false;
      if (filterMonth !== "all" && d.getMonth() !== parseInt(filterMonth)) return false;
    }
    return true;
  });

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  if (loading) return <div className="p-6 text-center text-slate-500">Cargando...</div>;
  if (!contacto) return null;

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="contacto-detalle-page">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/helpdesk/contactos")} data-testid="back-btn">
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-4 flex-1">
            {contacto.picture ? (
              <img src={contacto.picture} alt="" className="w-12 h-12 rounded-full" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center">
                <User className="w-6 h-6 text-slate-500" />
              </div>
            )}
            <div>
              <h1 className="text-xl font-bold text-slate-800">{contacto.nombre}</h1>
              <div className="flex items-center gap-3 text-sm text-slate-500">
                {contacto.email && (
                  <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {contacto.email}</span>
                )}
                {contacto.empresa && (
                  <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> {contacto.empresa}</span>
                )}
                <Badge className={contacto.tipo === "interno" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}>
                  {contacto.tipo === "interno" ? "Interno" : "Externo"}
                </Badge>
              </div>
            </div>
          </div>
          <Badge variant="outline" className="text-lg px-4 py-1">
            <Ticket className="w-4 h-4 mr-2" /> {tickets.length} ticket{tickets.length !== 1 ? "s" : ""}
          </Badge>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[160px]" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              {Object.entries(STATUS_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterMonth} onValueChange={setFilterMonth}>
            <SelectTrigger className="w-[150px]" data-testid="filter-month"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los meses</SelectItem>
              {MONTHS.map((m, i) => <SelectItem key={i} value={String(i)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterYear} onValueChange={setFilterYear}>
            <SelectTrigger className="w-[120px]" data-testid="filter-year"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los años</SelectItem>
              {years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-sm text-slate-500">{filtered.length} resultado{filtered.length !== 1 ? "s" : ""}</span>
        </div>

        {/* Tickets Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>FOLIO</TableHead>
                  <TableHead>TÍTULO</TableHead>
                  <TableHead>CATEGORÍA</TableHead>
                  <TableHead>PRIORIDAD</TableHead>
                  <TableHead>ESTADO</TableHead>
                  <TableHead>ASIGNADO A</TableHead>
                  <TableHead>FECHA</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400">No hay tickets con estos filtros</TableCell></TableRow>
                ) : filtered.map(t => {
                  const st = STATUS_MAP[t.status] || STATUS_MAP.abierto;
                  const pr = PRIORITY_MAP[t.priority] || PRIORITY_MAP.media;
                  return (
                    <TableRow key={t.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/helpdesk/tickets/${t.id}`)} data-testid={`ticket-row-${t.folio}`}>
                      <TableCell className="font-mono text-sm font-semibold text-blue-600">{t.folio}</TableCell>
                      <TableCell className="font-medium max-w-xs truncate">{t.title}</TableCell>
                      <TableCell><Badge variant="outline">{t.category}</Badge></TableCell>
                      <TableCell><Badge className={pr.color}><pr.icon className="w-3 h-3 mr-1" />{pr.label}</Badge></TableCell>
                      <TableCell><Badge className={st.color}>{st.label}</Badge></TableCell>
                      <TableCell className="text-sm">{t.assigned_to_name || "—"}</TableCell>
                      <TableCell className="text-sm text-slate-500">{fmtDate(t.created_at)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default HelpdeskContactoDetalle;
